import {
  BadgeCard,
  Button,
  Checkbox,
  DeleteIcon,
  GridColumn,
  GridRow,
  InfoIcon,
  Input,
  Select,
  SelectOptionType,
  Tooltip,
  ValueField,
} from '@bp/ui-components';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { useCreateSelectOptions } from '../../../../hooks/useCreateSelectOptions';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { SubjectContainerListDataType } from '../../../SubjectContainer/graphql/types';
import { LessonFormType } from '../../types';

type LessonUnitFormItemProps = {
  index: number;
  subjectContainer: SubjectContainerListDataType[] | undefined;
  disabled: boolean;
  onDelete: () => void;
};

export const LessonUnitFormItem = observer(
  ({ index, subjectContainer, disabled, onDelete }: LessonUnitFormItemProps) => {
    const { t } = useTranslation();

    const { values, setFieldValue, setFieldTouched, errors, handleChange } = useFormikContext<LessonFormType>();

    const [options, setOptions] = useState<SelectOptionType[]>([]);

    const { subjectData } = useLoadBasicData({ pause: false });

    //biome-ignore lint/correctness/useExhaustiveDependencies(t): ignore t
    useEffect(() => {
      const updatedOptions = [];
      if (values.subject) {
        const subject = subjectData?.subjects.find((s) => s.uuid === values.subject?.uuid);
        if (subject?.subjectHourEnabled) {
          updatedOptions.push({ value: 'subjectHour', label: t('subject.subjectHour') });
        }
        if (subject?.epochEnabled) {
          updatedOptions.push({ value: 'epoch', label: t('subject.epoch') });
        }
        setOptions(updatedOptions);
      }
    }, [values.subject, subjectData?.subjects]);

    const subjectContainerOptions = useCreateSelectOptions(subjectContainer, 'uuid', 'name');
    const lessonUnit = values.lessonUnit;

    //biome-ignore lint/correctness/useExhaustiveDependencies(setFieldValue): ignore setFieldValue
    useEffect(() => {
      if (lessonUnit && lessonUnit[index].lessonUnitType === 'subjectHour') {
        setFieldValue(`lessonUnit.${index}.subjectContainerUuid`, null);
      }
    }, [lessonUnit, index]); // don't add formik as dependency !!! ;

    if (!lessonUnit || !lessonUnit[index]) return;

    return (
      <GridRow spacingBottom='m' spacingTop='none'>
        <GridColumn width={10}>
          <GridRow spacingBottom='none' spacingTop='none'>
            <GridColumn width={6}>
              <Select
                readonly={disabled}
                label={t('lessonUnit.subjectType')}
                name={`lessonUnit.${index}.subjectType`}
                options={options}
                onChange={(option) => {
                  const opt = option as SingleValue<SelectOptionType>;
                  setFieldValue(`lessonUnit.${index}.lessonUnitType`, opt?.value);
                }}
                value={options.find((o) => o.value === lessonUnit[index].lessonUnitType)}
              />
            </GridColumn>
            <GridColumn width={6}>
              <Select
                readonly={disabled}
                required={lessonUnit[index].lessonUnitType === 'epoch'}
                label={t('lessonUnit.subjectContainer')}
                disabled={lessonUnit[index].lessonUnitType === 'subjectHour'}
                name={`lessonUnit.${index}.subjectContainerUuid`}
                options={subjectContainerOptions}
                onChange={async (option) => {
                  const opt = option as SingleValue<SelectOptionType>;
                  await setFieldTouched(`lessonUnit.${index}.subjectContainer`, true);
                  await setFieldValue(`lessonUnit.${index}.subjectContainerUuid`, opt?.value);
                }}
                error={
                  errors.lessonUnit
                    ? // @ts-expect-error
                      (errors.lessonUnit[index]?.subjectContainerUuid ?? undefined)
                    : undefined
                }
                value={
                  values.lessonUnit && values.lessonUnit[index]?.subjectContainerUuid
                    ? subjectContainerOptions.find((sc) => {
                        if (values.lessonUnit && values.lessonUnit[index]?.subjectContainerUuid) {
                          return sc.value === values.lessonUnit![index]?.subjectContainerUuid;
                        }
                        return false;
                      })
                    : null
                }
              />
            </GridColumn>
          </GridRow>
          <GridRow spacingBottom='none' spacingTop='none'>
            <GridColumn width={2}>
              <Input
                readonly={disabled}
                type='number'
                step={1}
                min={1}
                max={values.lessonUnit && values.lessonUnit[index]?.subjectContainerUuid ? 20 : 8}
                // @ts-expect-error
                error={errors.lessonUnit ? (errors.lessonUnit[index]?.duration ?? undefined) : undefined}
                label={t('lessonUnit.duration')}
                name={`lessonUnit.${index}.duration`}
                onChange={handleChange}
                value={values.lessonUnit ? (values.lessonUnit[index]?.duration ?? undefined) : undefined}
                suffix={
                  <div>
                    {lessonUnit[index].lessonUnitType === 'subjectHour'
                      ? t('common.hour', {
                          count:
                            values.lessonUnit && values.lessonUnit[index]?.duration
                              ? (values.lessonUnit[index]?.duration ?? 0)
                              : 0,
                        })
                      : t('common.week', {
                          count:
                            values.lessonUnit && values.lessonUnit[index]?.duration
                              ? (values.lessonUnit[index]?.duration ?? 0)
                              : 0,
                        })}
                  </div>
                }
              />
            </GridColumn>
            <GridColumn width={2}>
              <Input
                readonly={disabled}
                type='number'
                step={1}
                min={0}
                label={t('common.count')}
                name={`lessonUnit.${index}.count`}
                onChange={handleChange}
                value={values.lessonUnit ? (values.lessonUnit[index].count ?? undefined) : undefined}
              />
            </GridColumn>
            <GridColumn width={2}>
              <Input
                type='number'
                label={t(`lessonUnit.factor`)}
                name={`lessonUnit.${index}.factor`}
                readonly
                onChange={handleChange}
                value={values.lessonUnit ? (values.lessonUnit[index].factor ?? undefined) : undefined}
                suffix={
                  <Tooltip content={t('lessonUnit.tooltip.factor')}>
                    <InfoIcon className={'small'} />
                  </Tooltip>
                }
              />
            </GridColumn>
            <GridColumn width={3}>
              {values.lessonUnit && values.lessonUnit[index] && values.lessonUnit[index]?.duration && (
                <ValueField title={t('badgeCard.preview')} position='top'>
                  <BadgeCard
                    className='mt-2'
                    label={values.subject?.name.slice(0, 2).toUpperCase() ?? ''}
                    count={values.lessonUnit[index].count ?? 0}
                    width={Math.abs(Math.min(Math.floor(values.lessonUnit[index]?.duration ?? 0), 4)) as 1 | 2 | 3 | 4}
                  />
                </ValueField>
              )}
            </GridColumn>
            {values.lessonUnit?.[index].lessonUnitType === 'epoch' && (
              <GridColumn width={3} className='mt-3' align='end'>
                <Checkbox
                  name='isClassTrip'
                  checked={values.isClassTrip ?? false}
                  label={t('lesson.basics.isClassTrip')}
                  onChange={async (event) => {
                    await setFieldValue(`isClassTrip`, event.target.checked, true);
                    await setFieldTouched(`isClassTrip`, true);
                  }}
                />
              </GridColumn>
            )}
          </GridRow>
        </GridColumn>
        <GridColumn width={2} className='mt-5' align='end'>
          <Button hierarchy='tertiary' disabled={disabled} onClick={onDelete} icon={<DeleteIcon />} />
        </GridColumn>
      </GridRow>
    );
  },
);
